<docs>
# Datagrid

General element for showing table data.
</docs>

<template>
  <div class="datagrid-wrapper">
    <!-- Filters -->
    <div class="datagrid__header datagrid__header--filters">
      <div class="row">
        <div class="col text-right">
          <input
            v-model="search"
            :placeholder="$t('datagrid.search')"
            class="form-control"
          >
        </div>
        <div class="col fill datagrid__cta">
          <!--
          <router-link :to="{ name: createName }" :title="$t('general.create')" class="btn btn-flex btn-success">
            <font-awesome-icon icon="plus" />
            <span v-if="$te(`${entity}.actions.create`)">{{ $t(`${entity}.actions.create`) }}</span>
            <span v-else>{{ $t('general.create') }}</span>
          </router-link>
          -->
        </div>
      </div>
    </div>
    <!-- Operations -->
    <div class="datagrid__header datagrid__header--operations">
      <div class="row">
        <div class="col fill datagrid__cta">
          <Dropdown
            class="btn btn-flex btn-grid"
            :class="[{ 'disabled' : selected.length === 0 }]"
            :items="dropdownItems"
            :label="$t('general.bulk.actions')"
            caret="chevron-down"
          ></Dropdown>
        </div>
        <div class="col"></div>
        <div class="col datagrid__small-control">
          <select
            class="form-control"
            v-model="paginate"
          >
            <option
              v-for="perpage in pagination"
              :value="perpage"
              :key="perpage"
            >{{ perpage }}</option>
          </select>
        </div>
      </div>
    </div>
    <div class="datagrid__body">
      <div class="table-wrapper">
        <table class="datagrid">
          <thead>
            <th
              class="cell__checkbox"
              v-if="filtered.length > 0"
            >
              <input
                data-lpignore="true"
                type="checkbox"
                v-model="selectedAll"
              >
            </th>
            <template v-for="col in cols">
              <component
                v-if="componentExists(`heads${col.key}`)"
                :is="`heads${col.key}`"
                @click="orderBy = col.key"
                :orderBy="orderBy"
                :order="order"
                :entity="entity"
                :col="col.key"
                :key="`head-${col.key}`"
                :class="[{ 'active' : orderBy === col.key }]"
              ></component>
              <th
                v-else
                :key="`th-${col.key}`"
                @click="orderBy = col.key"
                class="sortable noselect"
                :class="[{ 'active' : orderBy === col.key }]"
              >
                <span class="datagrid__control">
                  <font-awesome-icon
                    icon="sort-down"
                    v-show="orderBy === col.key && order === 'DESC'"
                  />
                  <font-awesome-icon
                    icon="sort-up"
                    v-show="orderBy === col.key && order === 'ASC'"
                  />
                  <font-awesome-icon
                    icon="sort"
                    v-show="orderBy !== col.key"
                    class="muted"
                  />
                </span>
                {{ $t(`${entity}.cols.${col.key}`) }}
              </th>
            </template>
            <th class="text-right cell__actions">
              {{ $tc('datagrid.item_count', filtered.length, { count: filtered.length }) }}
            </th>
          </thead>
          <tbody v-if="entries.length > 0">
            <tr
              v-for="(entry, index) in filteredAndSorted"
              v-if="isPaginated(index)"
              :key="entry._id"
              @click="select(entry._id)"
            >
              <td class="cell__checkbox">
                <input
                  type="checkbox"
                  v-model="selected"
                  :value="entry._id"
                >
              </td>
              <template v-for="(col, colindex) in cols">
                <!-- Prio 0: {cellsObjectTypecol} cellswebsitelabel -->
                <component
                  v-if="componentExists(`cells${objectType}${col.key}`)"
                  :is="`cells${objectType}${col.key}`"
                  :col="col.key"
                  :entry="entry"
                  :key="`td-${col.key}`"
                  :edit-name="editName"
                  :flags="flags"
                ></component>
                <!-- Prio 1: {cellscol} cellsfirst_name -->
                <component
                  v-else-if="componentExists(`cells${col.key}`)"
                  :is="`cells${col.key}`"
                  :col="col.key"
                  :entry="entry"
                  :key="`td-${col.key}`"
                  :edit-name="editName"
                ></component>
                <!-- Prio 2: {cellstype} cellsicon -->
                <component
                  v-else-if="componentExists(`cells${col.type}`)"
                  :is="`cells${col.type}`"
                  :col="col.key"
                  :entry="entry"
                  :key="`td-${col.type}`"
                  :edit-name="editName"
                ></component>
                <!-- Prio 3: fallback -->
                <td
                  v-else
                  :class="[`cell-value-${col.key}`]"
                  :key="`cell-${col.key}`"
                >
                  <router-link
                    :to="{ name: editName, params: { id: entry._id } }"
                    v-if="colindex === 0"
                    class="cell__link--primary"
                  >
                    {{ entry[col.key] }}
                  </router-link>
                  <span v-else>
                    {{ entry[col.key] }}
                  </span>
                </td>
              </template>
              <component
                v-if="componentExists(`cellsactions${objectType}${entity}`)"
                v-on:pdf="pdf"
                v-on:mail="mail"
                v-on:trash="trash"
                :is="`cellsactions${objectType}${entity}`"
                :editName="editName"
                :entry="entry"
                :key="`td-actions-${entity}`"
              ></component>
              <component
                v-else-if="componentExists(`cellsactions${entity}`)"
                v-on:pdf="pdf"
                v-on:mail="mail"
                v-on:trash="trash"
                :is="`cellsactions${entity}`"
                :editName="editName"
                :entry="entry"
                :key="`td-actions-${entity}`"
              ></component>
              <td
                class="text-right cell__actions"
                v-on:dblclick="goEdit(entry)"
                v-else
              >
                <Dropdown
                  class="context-ellipsis"
                  :items="contextItems(entry)"
                  icon="ellipsis-v"
                ></Dropdown>
              </td>
            </tr>
          </tbody>
          <tbody
            class="datagrid__tbody--empty"
            v-else
          >
            <td>
              <p>{{ $t('datagrid.no_entries') }}</p>
              <p>
                {{ $t('datagrid.start_by') }}
                <router-link :to="{ name: createName }">{{ $t('datagrid.creating_cta') }}</router-link>
              </p>
            </td>
          </tbody>
        </table>
      </div>
    </div>
    <div class="datagrid__footer">
      <div class="row align-items-center">
        <div class="col">

        </div>
        <div class="col col__showing text-right">
          {{ $t('datagrid.showing', { min: showingMin, max: showingMax, results: resultsMax }) }}
        </div>
        <div
          class="col col__pagination text-right"
          v-if="pages.length > 1"
        >
          <ol class="datagrid__pagination pagination">
            <li
              class="pagination__arrow"
              :class="{ 'muted': page === 1, 'noclickable': page === 1 }"
            >
              <button
                class="btn"
                @click="page = page - 1"
              >
                <font-awesome-icon icon="chevron-left" />
              </button>
            </li>
            <li
              v-for="targetPage in pages"
              :key="targetPage"
              class="pagination__page"
              :class="{ 'active' : targetPage === page }"
            >
              <button
                class="btn"
                @click="page = targetPage"
              >
                {{ targetPage }}
              </button>
            </li>
            <li
              class="pagination__arrow"
              :class="{ 'muted': page === maxPages, 'noclickable': page === maxPages }"
            >
              <button
                class="btn"
                @click="page = page + 1"
              >
                <font-awesome-icon icon="chevron-right" />
              </button>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@/components/Dropdown';
import cellsicon from '@/components/cells/Icon';
import cellsref from '@/components/cells/Ref';
import cellsobject_type from '@/components/cells/Object_type';
import cellswebsitelabel from '@/components/cells/website/Label';
import cellswebsitedomains from '@/components/cells/website/Domains';
import cellsupload from '@/components/cells/Upload';
import cellscreatedAt from '@/components/cells/CreatedAt';
import cellsupdatedAt from '@/components/cells/UpdatedAt';
import headsicon from '@/components/heads/Icon';
import cellsactionsdefaultobjects from '@/components/cells/actions/DefaultObjects';
import { mapState } from 'vuex';
import { EventBus } from '@/event-bus.js';

export default {
  name: 'datagrid',
  components: {
    Dropdown,
    cellsicon,
    cellsref,
    cellswebsitelabel,
    cellswebsitedomains,
    cellsobject_type,
    headsicon,
    cellsactionsdefaultobjects,
    cellsupload,
    cellscreatedAt,
    cellsupdatedAt,
  },
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    entity: {
      type: String,
      default: () => false,
    },
    createNameOverride: {
      type: String,
      default: () => '',
    },
    editNameOverride: {
      type: String,
      default: () => '',
    },
    filterCols: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Array,
      default: () => [5, 10, 25, 50, 100],
    },
    collection: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    collectionMax: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      search: '',
      orderCol: null,
      orderWay: 'ASC',
      page: 1,
      paginate:
        parseInt(localStorage.getItem('datagrid/paginate'), 10) || this.perPage,
      selected: [],
      selectedAll: false,
      flags: [],
      listening: false,
      deploying: false,
    };
  },
  computed: {
    ...mapState({
      fields: state => state.fields.entries,
    }),
    dropdownItems() {
      return [
        {
          label: this.$te(`${this.entity}.actions.bulk.delete`)
            ? this.$t(`${this.entity}.actions.bulk.delete`)
            : this.$t('general.bulk.delete'),
          action: () => {
            this.bulk('delete');
          },
        },
      ];
    },
    pages() {
      const pages = [];
      for (let i = 0; i < this.maxPages; i++) {
        pages.push(i + 1);
      }
      return pages;
    },
    resultsMax() {
      if (this.collectionMax !== 0) {
        return this.collectionMax;
      }
      return this.filtered.length;
    },
    showingMin() {
      if (this.filtered.length === 0) {
        return 0;
      }
      return (this.page - 1) * this.paginate + 1;
    },
    showingMax() {
      const potential = this.page * this.paginate;
      if (potential > this.resultsMax) {
        return this.resultsMax;
      }
      return potential;
    },
    maxPages() {
      return Math.ceil(this.resultsMax / this.paginate);
    },
    orderBy: {
      get() {
        if (!this.orderCol) {
          return this.cols[0];
        }
        return this.orderCol;
      },
      set(value) {
        if (this.orderBy === value) {
          this.order = this.order === 'ASC' ? 'DESC' : 'ASC';
        }
        this.orderCol = value;
      },
    },
    order: {
      get() {
        return this.orderWay;
      },
      set(value) {
        this.orderWay = value;
      },
    },
    normalizedSearch() {
      return this.normalize(this.search);
    },
    filteredAndSorted() {
      return this.sort(this.filtered);
    },
    filtered() {
      if (!this.search) {
        return this.entries;
      }

      const result = this.entries.filter(element => {
        let include = false;
        Object.keys(element).forEach(key => {
          if (this.normalize(element[key]) === this.normalizedSearch) {
            include = true;
          } else if (
            typeof element[key].indexOf === 'function' &&
            this.normalize(element[key]).indexOf(this.normalizedSearch) !== -1
          ) {
            include = true;
          }
        });
        return include;
      });
      return result;
    },
    cols() {
      if (typeof this.entries[0] === 'undefined') {
        return [];
      }

      const showing = Object.keys(this.entries[0]).filter(element => {
        return (
          [
            '_id',
            '__v',
            'ssh_port',
            'ssh_user',
            'server',
            'password',
            '$id',
            'id',
            '$isUpdating',
            '$updateErrors',
            '$isDeleting',
            '$deleteErrors',
          ]
            .concat(this.filterCols)
            .indexOf(element) === -1
        );
      });

      const cols = [];

      // Add field information to cols if available
      showing.forEach(col => {
        const field = this.fields.find(
          element =>
            (element.object_type === this.objectType ||
              this.objectType === 'default') &&
            element.key === col,
        );
        if (field) {
          cols.push(field);
        } else {
          cols.push({
            key: col,
          });
        }
      });

      return cols;
    },
    createName() {
      if (this.createNameOverride !== '') {
        return this.createNameOverride;
      }
      return `${this.entity}-create`;
    },
    editName() {
      if (this.editNameOverride !== '') {
        return this.editNameOverride;
      }
      return `${this.entity}-id`;
    },
    objectType() {
      if (this.$route.params.type) {
        return this.$route.params.type;
      }
      return 'default';
    },
  },
  created() {
    this.$store.dispatch(`fields/get`);

    if (
      !EventBus['objects/website/created'] ||
      !EventBus['objects/website/created'].registered
    ) {
      EventBus.$off('objects/website/created', this.deployer);
      EventBus.$on('objects/website/created', this.deployer);
      EventBus['objects/website/created'] = {
        registered: true,
      };
    }
  },
  methods: {
    deployer(data) {
      if (data && data.body) {
        this.deploy(data._id, data.body);
      }
    },
    goEdit(entry) {
      this.$router.push({
        name: this.editName,
        params: { id: entry._id },
      });
    },
    contextItems(entry) {
      if (this.entity === 'objects' && this.objectType === 'website') {
        return [
          {
            label: this.$t('general.edit'),
            action: () => {
              this.$router.push({
                name: this.editName,
                params: { id: entry._id },
              });
            },
          },
          {
            label: 'Deploy',
            action: () => {
              this.deploy(entry._id);
            },
          },
          {
            label: 'Delete Website',
            action: () => {
              this.deleteWebsite(entry._id);
            },
          },
          {
            label: 'Get info',
            action: () => {
              this.minicp(entry._id);
            },
          },
          {
            label: 'Create default user',
            action: () => {
              this.minicpuser(entry._id);
            },
          },
        ];
      }

      return [
        {
          label: this.$t('general.edit'),
          action: () => {
            this.$router.push({
              name: this.editName,
              params: { id: entry._id },
            });
          },
        },
        {
          label: this.$t('general.pdf'),
          action: () => {
            this.pdf(entry._id);
          },
        },
        {
          label: this.$t('general.mail'),
          action: () => {
            this.mail(entry._id);
          },
        },
        {
          label: this.$t('general.delete'),
          action: () => {
            this.trash(entry._id);
          },
        },
      ];
    },
    get(id) {
      return this.entries.find(item => item._id === id);
    },
    select(id) {
      const index = this.selected.indexOf(id);
      if (index === -1) {
        this.selected.push(id);
      } else {
        this.selected.splice(index, 1);
      }
    },
    unselectAll() {
      this.selected = [];
    },
    selectAll() {
      this.filtered.forEach(entry => {
        if (this.selected.indexOf(entry._id) === -1) {
          this.selected.push(entry._id);
        }
      });
    },
    normalize(input) {
      return `${input}`.toLowerCase();
    },
    isPaginated(index) {
      return (
        index >= (this.page - 1) * this.paginate &&
        index < this.page * this.paginate
      );
    },
    sort(entries) {
      return this.decouple(entries).sort((a, b) => {
        if (a[this.orderBy] < b[this.orderBy]) {
          return this.orderWay === 'ASC' ? -1 : 1;
        }
        if (a[this.orderBy] > b[this.orderBy]) {
          return this.orderWay === 'ASC' ? 1 : -1;
        }
        return 0;
      });
    },
    mail(id) {
      this.$http
        .get(`/mail`, { id })
        .then(() => {
          this.$success(this.$t('general.messages.mail.success'));
        })
        .catch(() => {
          this.$error(this.$t('general.messages.mail.fail'));
        });
    },
    pdf(id) {
      console.log(id);
      window.location.href = 'http://localhost:3000/pdf';
    },
    bulk(action) {
      if (action === 'delete') {
        this.bulkDelete();
      }
    },
    bulkDelete() {
      this.$dialog
        .confirm(
          this.$tc('general.confirm.bulk_delete', this.selected.length, {
            count: this.selected.length,
          }),
          {
            okText: this.$t('general.confirm.ok'),
            cancelText: this.$t('general.confirm.cancel'),
            animation: 'bounce',
          },
        )
        .then(() => {
          this.selected.forEach(id => {
            this.$store.dispatch(`${this.entity}/delete`, {
              id,
              callback: () => {
                this.$success(this.$t('general.messages.delete.success'));
              },
            });
          });
        })
        .catch(() => {
          this.$info(this.$t('general.messages.delete.abort'));
        });
    },
    trash(id) {
      this.$dialog
        .confirm(this.$t('general.confirm.delete'), {
          okText: this.$t('general.confirm.ok'),
          cancelText: this.$t('general.confirm.cancel'),
          animation: 'bounce',
        })
        .then(() => {
          this.$store.dispatch(`${this.entity}/delete`, {
            id,
            callback: () => {
              this.$success(this.$t('general.messages.delete.success'));
            },
          });
        })
        .catch(() => {
          this.$info(this.$t('general.messages.delete.abort'));
        });
    },
    addFlag(id, flag) {
      this.flags.push({
        id,
        flag,
      });
    },
    removeFlag(id, flag) {
      Object.keys(this.flags).forEach(key => {
        const item = this.flags[key];
        if (item.id === id && item.flag === flag) {
          this.flags.splice(key, 1);
        }
      });
    },
    deleteWebsite(id, websiteData) {
      let website = websiteData;
      if (typeof websiteData === 'undefined') {
        website = this.get(id);
      }

      this.find({
        object: this.entity,
        type: 'server',
        id: website.server,
        callback: server => {
          // Get subdomain from the dev domain
          const subs = website.dev_domain.split('.');
          website.sub = subs[0];

          this.$info(`Deleting started for ${website.dev_domain}`);

          this.$store.dispatch(`${this.entity}/deleteWebsite`, {
            website,
            server,
            callback: data => {
              if (data.type === 'success') {
                this.$success(`Deleting finished for ${website.dev_domain}`);
              } else if (data.type === 'error') {
                this.$error(
                  `Deleting failed for ${website.dev_domain} due to ${
                    data.error.level
                  }`,
                );
              }
            },
          });
        },
      });
    },
    deploy(id, websiteData) {
      if (this.deploying) {
        return false;
      }

      this.deploying = true;

      let website = websiteData;
      if (typeof websiteData === 'undefined') {
        website = this.get(id);
      }
      EventBus.$emit('objects/website/deploying', true);

      // Analytics
      this.$ga.event('website', 'deploy', website.dev_domain);

      console.log(website);

      this.find({
        object: this.entity,
        type: 'server',
        id: website.server,
        callback: server => {
          // Get subdomain from the dev domain
          const subs = website.dev_domain.split('.');
          website.sub = subs[0];
          this.$info(`Deployment started for ${website.dev_domain}`);
          this.addFlag(id, 'deploying');
          this.$store.dispatch(`${this.entity}/deploy`, {
            website,
            server,
            callback: data => {
              this.removeFlag(id, 'deploying');
              EventBus.$emit('objects/website/deploying', false);
              this.deploying = false;
              if (data.type === 'success') {
                this.$success(`Deployment finished for ${website.dev_domain}`);
              } else if (data.type === 'error') {
                this.$error(
                  `Deployment failed for ${website.dev_domain} due to ${
                    data.error.level
                  }`,
                );
              }
            },
          });
        },
      });
    },
    syntaxHighlight(json) {
      if (typeof json != 'string') {
        json = JSON.stringify(json, undefined, 2);
      }
      json = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        function(match) {
          var cls = 'number';
          if (/^"/.test(match)) {
            if (/:$/.test(match)) {
              cls = 'key';
            } else {
              cls = 'string';
            }
          } else if (/true|false/.test(match)) {
            cls = 'boolean';
          } else if (/null/.test(match)) {
            cls = 'null';
          }
          return '<span class="' + cls + '">' + match + '</span>';
        },
      );
    },
    minicp(id) {
      const website = this.get(id);
      const requestUrl = `https://${website.dev_domain}/wp-json/mini-cp/info/`;
      this.$info(`Requesting remote information from ${website.dev_domain}`);
      this.$http
        .get(requestUrl)
        .then(data => {
          this.$dialog
            .confirm(
              `<h3>Info</h3><pre style="max-height:250px;overflow-y:auto;font-size:10px;word-wrap: break-word;">${this.syntaxHighlight(
                JSON.stringify(data, undefined, 2),
              )}</pre>`,
              {
                html: true,
                okText: 'Ok',
                cancelText: 'Zavřít',
              },
            )
            .then(() => {
              console.log('Clicked on proceed');
            })
            .catch(() => {
              console.log('Clicked on cancel');
            });
          this.$success(
            `Information succesfully requested from ${website.dev_domain}`,
          );
        })
        .catch(error => {
          this.$error(
            `Requesting remote information from ${
              website.dev_domain
            } failed due to ${error.code} ${error.text}`,
          );
        });
    },
    createWpUser(user, website) {
      const requestUrl = `https://${website.dev_domain}/wp-json/wp/v2/users`;
      this.$info(`Create user operation for ${website.dev_domain} initiated`);
      const client = this.$http.create({
        headers: {
          Authorization:
            'Basic Y29udHJvbHVzZXI6dEJ3VSBsa05IIHdOcm0gWTlrcCBwUGoyIE1MN0c=', // echo -n "USERNAME:PASSWORD" | base64 (it's Application password)
        },
      });

      client
        .post(requestUrl, {
          username: user.username,
          email: user.email,
          password: user.password,
          locale: 'cs_CZ',
          roles: ['administrator'],
        })
        .then(data => {
          console.log({
            url: website.dev_domain,
            data,
          });
          this.$success(
            `Create user operation for ${
              website.dev_domain
            } succesfully finished`,
          );
        })
        .catch(error => {
          this.$error(
            `Create user operation for ${website.dev_domain} failed due to ${
              error.response.data.code
            } ${error.response.data.message}`,
          );
        });
    },
    minicpuser(id) {
      const website = this.get(id);
      this.findBy({
        object: 'objects',
        type: 'wordpress-user',
        field: 'user',
        id: this.user._id,
        callback: data => {
          this.createWpUser(data, website);
        },
        error: () => {
          this.$error(
            'Wordpress User for your currently logged in user could not be found, make sure you have Wordpress User to your Deploy User',
          );
        },
      });
    },
  },
  watch: {
    page(value) {
      this.$emit('paginated', value);
    },
    paginate(value) {
      localStorage.setItem('datagrid/paginate', value);
    },
    maxPages(value) {
      if (value < this.page) {
        this.page = 1;
      }
    },
    selectedAll(value) {
      if (value) {
        this.selectAll();
      } else {
        this.unselectAll();
      }
    },
  },
};
</script>

<style lang="scss">
$border: $muted;
$th-bg: $inverse;
$odd-bg: $inverse;
$hover-bg: darken($odd-bg, 5%);
$cell-height: 60px;

.datagrid-wrapper {
  margin-bottom: 10px;
}

.datagrid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  th,
  td {
    height: $cell-height;
    border: 1px solid $border;
  }
  th {
    padding: 0.5em;
    font-size: $base-font-size * 0.8;
  }
  td {
    padding: 1em 0.5em;
  }
  th {
    border-bottom: 0;
    background-color: $th-bg;
    &.sortable {
      cursor: pointer;
      .datagrid__control {
        opacity: 0;
      }
      &:hover {
        .datagrid__control {
          opacity: 0.4;
        }
      }
      &.active {
        color: $link;
        .datagrid__control {
          opacity: 1;
        }
      }
    }
    &:not(.text-right) {
      text-align: left;
    }
    .datagrid__control {
      float: right;
    }
  }
  tr {
    &:nth-child(odd) {
      td {
        background-color: $odd-bg;
      }
    }
    &:hover {
      td {
        background-color: $hover-bg;
      }
    }
    & > td,
    & > th {
      border-left-width: 0;
      border-right-width: 0;
      &:first-child {
        border-left-width: 1px;
      }
      &:last-child {
        border-right-width: 1px;
      }
    }
  }

  thead {
    border-left: 1px solid $border;
    border-right: 1px solid $border;
    th {
      color: $fill-default;
      border-right: 0;
      border-left: 0;
    }
  }

  tbody {
    tr:first-child {
      td {
        border-top: 0;
      }
    }
  }
}

.datagrid__header,
.datagrid__footer,
.datagrid__body {
  padding: 10px;
  border: 1px solid $border;
  &.datagrid__header {
    border-bottom: 0;
  }
  &.datagrid__footer {
    border-top: 0;
  }
  &.datagrid__body {
    border-top: 0;
    border-bottom: 0;
    padding: 0;
    .table-wrapper {
      margin-left: -1px;
      margin-right: -1px;
    }
  }
}

.datagrid__header {
  .form-control {
    height: $cell-height;
    border-color: transparent;
  }
}

.datagrid__footer {
  line-height: $cell-height;
}

.datagrid__tbody--empty {
  td {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.datagrid__pagination {
  list-style-type: none;
  padding: 0;
  margin: 0;
  li {
    display: inline-block;
    &.active {
      .btn {
        background-color: $border;
      }
    }
    &.pagination__page {
      display: none;
    }
    &.pagination__arrow {
      .btn {
        border: transparent;
      }
    }
  }
}

.col__pagination {
  max-width: 120px;
}

.datagrid__cta {
  max-width: 180px;
  width: 180px;
  .btn {
    position: relative;
    .fa-plus {
      position: absolute;
      left: 20px;
    }
  }
}
.datagrid__small-control {
  max-width: 75px;
  width: 75px;
}
.cell__actions {
  width: 80px;
  .icon {
    font-size: 0.7em;
  }
}
.cell__checkbox {
  width: 50px;
  text-align: center !important;
}

.text-right {
  .btn-group {
    float: right;
  }
}

.context-ellipsis {
  display: inline-block;
  padding: 0 15px;
  cursor: pointer;
  .dropdown-menu {
    width: 150px;
    left: auto;
  }
}

.cell__link--primary {
  font-weight: 700;
}

.label-deploying {
  opacity: 0.2;
}

.text-primary {
  color: $link;
}

pre {
  outline: 1px solid #ccc;
  padding: 5px;
  margin: 5px;
}
.string {
  color: green;
}
.number {
  color: darkorange;
}
.boolean {
  color: blue;
}
.null {
  color: magenta;
}
.key {
  color: red;
}
</style>

<i18n>
{
  "en": {
    "datagrid": {
      "item_count": "no items | one item | {count} items",
      "no_entries": "There are no entries for this data",
      "start_by": "Start by",
      "creating_cta": "creating a new entry",
      "search": "Search",
      "showing": "Showing {min} to {max} of {results}"
    }
  }
}
</i18n>
